import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import type { JSX } from "solid-js";
import { createSignal, onMount, Show } from "solid-js";
import { toast } from "solid-sonner";
import { z } from "zod";
import BankTransactionTable from "~/components/bank-transaction/bank-transaction-table";
import {
  IconInfo,
  IconLandmark,
  IconLink,
  IconPencil,
  IconPiggyBank,
  IconTrash,
} from "~/components/icons";
import { NotFound } from "~/components/not-found";
import { StandardRouteAssociationsPanel } from "~/components/standard-route/detail/associations-panel";
import { StandardRouteDetailCard } from "~/components/standard-route/detail/detail-card";
import { StandardRouteDetailEditCard } from "~/components/standard-route/detail/edit-card";
import { StandardRouteDetailHeader } from "~/components/standard-route/detail/header";
import {
  StandardRouteDetailGrid,
  StandardRouteDetailLeftColumn,
  StandardRouteDetailMode,
  StandardRouteDetailRightCard,
  StandardRouteDetailRightCardHeader,
  StandardRouteDetailRightColumn,
  StandardRouteDetailTabs,
  StandardRouteDetailTabsContent,
  StandardRouteDetailTabsStack,
} from "~/components/standard-route/detail/layout";
import { Button } from "~/components/ui/button";
import { CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "~/components/ui/dropdown-menu";
import { Col, Grid } from "~/components/ui/grid";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  TextField,
  TextFieldErrorMessage,
  TextFieldInput,
  TextFieldLabel,
} from "~/components/ui/text-field";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { AccountsAllResource } from "~/features/account/account.resource";
import { prefetchAssociations } from "~/features/association/association.prefetch";
import { getBankAccountDetailPageData } from "~/features/bank-account/bank-account-detail-page";
import { deleteBankAccount } from "~/features/bank-account/bank-account.api";
import { useBankAccountForm } from "~/features/bank-account/bank-account.form";
import { BankAccountDetailResource } from "~/features/bank-account/bank-account.resource";
import { bankAccountTypeLabel } from "~/features/bank-account/bank-account.schema";
import type { BankAccountItem } from "~/features/bank-account/bank-account.server";
import {
  BankTransactionCategoriesResource,
  BankTransactionResource,
} from "~/features/bank-transaction/bank-transaction.resource";
import {
  businessDetailResource,
  businessesResource,
} from "~/features/business/business.resource";
import { withBusinessGuard } from "~/lib/route-guards";
import { formatCurrency, formatDisplayCalendarDate } from "~/lib/utils";

export const Route = createFileRoute(
  "/_dashboard/dashboard/bank-accounts/$bankAccountId",
)({
  staticData: {
    breadcrumb: (m) => m.loaderData?.bankAccount?.name ?? "Bank Account",
  },
  validateSearch: z.object({
    mode: z.enum(["edit"]).optional(),
  }),
  // Route-specific 404 message (takes precedence over the dashboard-wide
  // notFoundComponent). Shown when a bank account doesn't exist — e.g. it was
  // deleted, or an AI agent linked here by mistake.
  notFoundComponent: () => (
    <NotFound
      title="Bank account not found"
      message="This bank account doesn't exist. It may have been deleted, or an agent may have linked you here by mistake."
      homeTo="/dashboard/bank-accounts"
      homeLabel="Go to bank accounts"
    />
  ),
  ...withBusinessGuard(),
  loader: async ({ params, context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;

    const [pageData, allAccounts] = await Promise.all([
      params.bankAccountId !== "new"
        ? getBankAccountDetailPageData({
            data: {
              businessId,
              bankAccountId: params.bankAccountId,
            },
          })
        : Promise.resolve(null),
      context.queryClient.ensureQueryData(
        AccountsAllResource.options({ businessId }),
      ),
    ]);

    const businessDetail = pageData?.businessDetail ?? null;
    const bankAccount = pageData?.bankAccount ?? null;

    if (bankAccount) {
      // Seed the resource cache so the detail query resolves from cache on
      // first render (mirrors the document detail page pattern).
      BankAccountDetailResource.seed(
        context.queryClient,
        { businessId, id: params.bankAccountId },
        bankAccount,
      );
    } else if (params.bankAccountId === "new") {
      context.queryClient.setQueryData(
        BankAccountDetailResource.key({
          businessId,
          id: params.bankAccountId,
        }),
        null,
      );
    }

    // Prefetch the detail page's component-level queries so they resolve from
    // cache on mount instead of flashing loading states. Fire-and-forget (not
    // awaited) so it never blocks the loader's critical path or SSR.
    if (params.bankAccountId !== "new") {
      prefetchAssociations(
        context.queryClient,
        businessId,
        "bank-account",
        params.bankAccountId,
      );
      // The default "Transactions" tab renders a BankTransactionTable, which
      // fires the transactions list + category facets on mount.
      void context.queryClient.prefetchQuery(
        BankTransactionResource.options({
          businessId,
          bankAccountIds: [params.bankAccountId],
          limit: 20,
          sortBy: "date",
          sortDir: "desc",
        }),
      );
      void context.queryClient.prefetchQuery(
        BankTransactionCategoriesResource.options({ businessId }),
      );
    }

    return { businessId, businessDetail, bankAccount, allAccounts };
  },
  component: BankAccountDetailPage,
});

type Mode = "view" | "edit";

// -- Page ----------------------------------------------------------------------

function BankAccountDetailPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const params = Route.useParams();
  const data = Route.useLoaderData();

  businessDetailResource.seedIfEmpty(
    qc,
    data().businessId,
    data().businessDetail,
  );
  AccountsAllResource.seedIfEmpty(
    qc,
    { businessId: data().businessId },
    data().allAccounts,
  );
  if (data().bankAccount) {
    BankAccountDetailResource.seedIfEmpty(
      qc,
      { businessId: data().businessId, id: params().bankAccountId },
      data().bankAccount!,
    );
  } else if (params().bankAccountId === "new") {
    // Seed null into QueryClient for "new" to prevent SSR deferred stream hang
    const newKey = BankAccountDetailResource.key({
      businessId: data().businessId,
      id: params().bankAccountId,
    });
    if (qc.getQueryData(newKey) === undefined) {
      qc.setQueryData(newKey, null);
    }
  }

  const isNew = () => params().bankAccountId === "new";
  const search = Route.useSearch();
  const initialMode: Mode = search().mode === "edit" ? "edit" : "view";
  const [mode, setMode] = createSignal<Mode>(initialMode);

  onMount(() => {
    if (initialMode === "edit") {
      const url = new URL(window.location.href);
      url.searchParams.delete("mode");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  });

  const bankAccountQuery = createQuery(() => ({
    ...BankAccountDetailResource.options(
      { businessId: data().businessId, id: params().bankAccountId },
      !isNew(),
    ),
    placeholderData: keepPreviousData,
  }));

  return (
    <div
      class={`flex flex-1 flex-col px-4 sm:px-6 pb-3 pt-10 ${
        !isNew() && mode() === "view"
          ? "@5xl/main:h-[calc(100svh-4.5rem)] @5xl/main:max-h-[calc(100svh-4.5rem)] @5xl/main:min-h-0 @5xl/main:overflow-auto"
          : ""
      }`}
    >
      <Show when={isNew()}>
        <BankAccountEdit
          bankAccount={null}
          businessId={data().businessId}
          allAccounts={data().allAccounts}
          onCancel={() => navigate({ to: "/dashboard/bank-accounts" })}
          onSaved={() => navigate({ to: "/dashboard/bank-accounts" })}
        />
      </Show>
      <Show when={!isNew()}>
        <Show
          when={bankAccountQuery.data}
          fallback={
            <div role="status" class="w-full animate-pulse">
              <div class="h-150 bg-muted rounded-lg w-full" />
            </div>
          }
        >
          {(bankAccount) => (
            <Show
              when={mode() === "edit"}
              fallback={
                <BankAccountView
                  bankAccount={bankAccount()}
                  businessId={data().businessId}
                  setMode={setMode}
                  onDeleted={() => {
                    BankAccountDetailResource.remove(qc, {
                      businessId: data().businessId,
                      id: params().bankAccountId,
                    });
                    // Remove the list cache so the table doesn't flash the
                    // deleted row before a background refetch.
                    qc.removeQueries({
                      queryKey: ["bank-account", data().businessId],
                    });
                    navigate({ to: "/dashboard/bank-accounts" });
                  }}
                />
              }
            >
              <BankAccountEdit
                bankAccount={bankAccount()}
                businessId={data().businessId}
                allAccounts={data().allAccounts}
                onCancel={() => setMode("view")}
                onSaved={() => {
                  BankAccountDetailResource.invalidate(qc, {
                    businessId: data().businessId,
                    id: params().bankAccountId,
                  });
                  setMode("view");
                }}
              />
            </Show>
          )}
        </Show>
      </Show>
    </div>
  );
}

// -- Delete Dialog -------------------------------------------------------------

function DeleteBankAccountDialog(props: {
  bankAccount: BankAccountItem;
  businessId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) {
  const [isDeleting, setIsDeleting] = createSignal(false);

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await deleteBankAccount({
        data: { id: props.bankAccount.id, businessId: props.businessId },
      });
      props.onDeleted();
    } catch {
      toast.error("Failed to delete bank account");
    } finally {
      setIsDeleting(false);
      props.onOpenChange(false);
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent class="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Bank Account</DialogTitle>
          <DialogDescription>
            This will permanently delete{" "}
            <span class="font-semibold">"{props.bankAccount.name}"</span>. This
            action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => props.onOpenChange(false)}
            disabled={isDeleting()}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting()}
          >
            <IconTrash class="mr-2 size-4" />
            {isDeleting() ? "Deleting..." : "Delete Bank Account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -- View Mode ----------------------------------------------------------------

function BankAccountView(props: {
  bankAccount: BankAccountItem;
  businessId: string;
  setMode: (mode: Mode) => void;
  onDeleted: () => void;
}) {
  const [deleteOpen, setDeleteOpen] = createSignal(false);

  const linkedAccountLabel = () => {
    const { accountCode, accountName } = props.bankAccount;
    if (accountCode && accountName) return `${accountCode} — ${accountName}`;
    return accountCode ?? accountName ?? "—";
  };

  return (
    <div class="flex flex-1 flex-col gap-6">
      <StandardRouteDetailHeader
        icon={<IconPiggyBank class="size-5" />}
        title={props.bankAccount.name}
        badge="Bank Account"
        menuContent={
          <>
            <DropdownMenuItem onClick={() => props.setMode("edit")}>
              <IconPencil class="size-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              class="text-destructive focus:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <IconTrash class="size-4" />
              Delete
            </DropdownMenuItem>
          </>
        }
      />

      <StandardRouteDetailMode mode="content">
        <StandardRouteDetailGrid>
          <StandardRouteDetailLeftColumn>
            <div class="space-y-6">
              <StandardRouteDetailCard
                icon={<IconLandmark class="size-5" />}
                title="Bank Account Details"
                contentClass=""
                action={
                  <Tooltip>
                    <TooltipTrigger
                      as={Button<"button">}
                      variant="ghost"
                      size="icon"
                      class="size-7 text-muted-foreground hover:text-foreground"
                      aria-label="Edit bank account"
                      onClick={() => props.setMode("edit")}
                    >
                      <IconPencil class="size-4" />
                    </TooltipTrigger>
                    <TooltipContent>Edit</TooltipContent>
                  </Tooltip>
                }
              >
                {/* Balance is the single most important accounting fact — make it the hero */}
                <div class="pb-5">
                  <p class="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                    Balance
                  </p>
                  <p class="mt-1.5 text-3xl font-semibold tabular-nums tracking-tight">
                    {props.bankAccount.balance != null
                      ? formatCurrency(props.bankAccount.balance)
                      : "—"}
                  </p>
                </div>

                <div class="divide-y divide-border border-t">
                  <InfoRow label="Name" value={props.bankAccount.name} />
                  <InfoRow
                    label="Subtype"
                    value={bankAccountTypeLabel(props.bankAccount.type)}
                  />
                  <InfoRow
                    label="Linked Account"
                    value={linkedAccountLabel()}
                  />
                  <InfoRow
                    label="Linked Account Type"
                    value={accountTypeLabel(props.bankAccount.accountType)}
                  />
                  <InfoRow
                    label="Description"
                    value={props.bankAccount.description}
                    multiline
                  />
                </div>
              </StandardRouteDetailCard>
            </div>
          </StandardRouteDetailLeftColumn>
          <StandardRouteDetailRightColumn>
            <StandardRouteDetailRightCard>
              <StandardRouteDetailRightCardHeader>
                <StandardRouteDetailTabs
                  defaultValue="transactions"
                  class="flex flex-col"
                >
                  <TabsList class="w-full">
                    <TabsTrigger value="transactions" class="flex-1">
                      <IconLandmark class="mr-2 size-4" />
                      <span class="hidden sm:inline">Transactions</span>
                    </TabsTrigger>
                    <TabsTrigger value="record" class="flex-1">
                      <IconInfo class="mr-2 size-4" />
                      <span class="hidden sm:inline">Record Info</span>
                    </TabsTrigger>
                    <TabsTrigger value="associations" class="flex-1">
                      <IconLink class="mr-2 size-4" />
                      <span class="hidden sm:inline">Associations</span>
                    </TabsTrigger>
                  </TabsList>

                  <StandardRouteDetailTabsStack>
                    <StandardRouteDetailTabsContent
                      value="transactions"
                      class="overflow-auto"
                      forceMount
                    >
                      {/* p-1 gives the search input's focus ring room to
                          render — the wrapper clips ring-offset otherwise. */}
                      <CardContent class="p-1">
                        <BankTransactionTable
                          businessId={props.businessId}
                          bankAccountIds={[props.bankAccount.id]}
                          defaultVisibility={{
                            bankAccountName: false,
                            institutionName: false,
                          }}
                        />
                      </CardContent>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="record"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <div class="divide-y divide-border">
                          <InfoRow
                            label="Created"
                            value={formatDisplayCalendarDate(
                              new Date(props.bankAccount.createdAt),
                            )}
                          />
                          <InfoRow
                            label="Updated"
                            value={formatDisplayCalendarDate(
                              new Date(props.bankAccount.updatedAt),
                            )}
                          />
                          <InfoRow
                            label="Business"
                            value="Active business bank account record"
                          />
                        </div>
                      </CardContent>
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="associations"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <StandardRouteAssociationsPanel
                          source={{
                            type: "bank-account",
                            id: props.bankAccount.id,
                          }}
                          businessId={props.businessId}
                          tabs={[
                            {
                              type: "bank-transaction",
                              defaultVisibility: {
                                status: false,
                                institutionName: false,
                                mappedAccountCode: false,
                                bankAccountName: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "journal-entry",
                              defaultVisibility: {
                                reference: false,
                                entries: false,
                              },
                            },
                            {
                              type: "document",
                              defaultVisibility: {
                                type: false,
                                amount: false,
                              },
                            },
                            {
                              type: "fixed-asset",
                              defaultVisibility: {
                                purchaseDate: false,
                                depreciationMethod: false,
                                purchaseCost: false,
                                updatedAt: false,
                              },
                            },
                            {
                              type: "worksheet",
                              defaultVisibility: {
                                description: false,
                                type: false,
                                updatedAt: false,
                              },
                            },
                          ]}
                        />
                      </CardContent>
                    </StandardRouteDetailTabsContent>
                  </StandardRouteDetailTabsStack>
                </StandardRouteDetailTabs>
              </StandardRouteDetailRightCardHeader>
            </StandardRouteDetailRightCard>
          </StandardRouteDetailRightColumn>
        </StandardRouteDetailGrid>
      </StandardRouteDetailMode>

      <DeleteBankAccountDialog
        bankAccount={props.bankAccount}
        businessId={props.businessId}
        open={deleteOpen()}
        onOpenChange={setDeleteOpen}
        onDeleted={props.onDeleted}
      />
    </div>
  );
}

// -- Edit / Create Form -------------------------------------------------------

function BankAccountEdit(props: {
  bankAccount: BankAccountItem | null;
  businessId: string;
  allAccounts: { id: string; code: string; name: string }[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isNew = () => props.bankAccount === null;
  const [serverError, setServerError] = createSignal("");

  const form = useBankAccountForm(props.businessId, props.bankAccount, {
    onSuccess: () => {
      toast.success(
        isNew()
          ? "Bank account created successfully"
          : "Bank account updated successfully",
      );
      props.onSaved();
    },
    onError: (e) =>
      setServerError(
        e instanceof Error ? e.message : "An unexpected error occurred",
      ),
  });

  return (
    <StandardRouteDetailEditCard
      title={isNew() ? "Add Bank Account" : "Edit Bank Account"}
      description={
        isNew()
          ? "Create a new bank account record."
          : "Update the bank account details. Changes apply immediately on save."
      }
      formId="bank-account-edit-form"
      onCancel={props.onCancel}
      saveButton={
        <form.Subscribe>
          {(state) => (
            <Button
              type="submit"
              form="bank-account-edit-form"
              disabled={state().isSubmitting}
            >
              {state().isSubmitting
                ? "Saving..."
                : isNew()
                  ? "Add Bank Account"
                  : "Save Changes"}
            </Button>
          )}
        </form.Subscribe>
      }
    >
      <form
        id="bank-account-edit-form"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Grid cols={1} colsMd={2} colsLg={3} class="gap-4">
          <Col span={1} spanLg={2}>
            <form.Field name="name">
              {(field) => (
                <FormField label="Name" required field={field()}>
                  <TextFieldInput
                    value={field().state.value}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="Bank account name"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="accountId">
              {(field) => (
                <FormField
                  label="Linked Chart Account"
                  required
                  field={field()}
                >
                  <Select
                    value={field().state.value || undefined}
                    onChange={(v) => v && field().handleChange(v)}
                    options={props.allAccounts.map((a) => a.id)}
                    placeholder="Select a chart account"
                    itemComponent={(p) => (
                      <SelectItem item={p.item}>
                        {accountOptionLabel(props.allAccounts, p.item.rawValue)}
                      </SelectItem>
                    )}
                  >
                    <SelectTrigger>
                      <SelectValue<string>>
                        {(state) =>
                          accountOptionLabel(
                            props.allAccounts,
                            state.selectedOption(),
                          )
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent />
                  </Select>
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col span={1} spanLg={3}>
            <form.Field name="description">
              {(field) => (
                <FormField label="Description" field={field()}>
                  <TextFieldInput
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                    placeholder="Optional description of this bank account"
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
        </Grid>

        <Show when={serverError()}>
          <p class="mt-4 text-sm text-destructive">{serverError()}</p>
        </Show>
      </form>
    </StandardRouteDetailEditCard>
  );
}

function accountOptionLabel(
  accounts: { id: string; code: string; name: string }[],
  id: string | undefined,
) {
  if (!id) return "Select a chart account";
  const account = accounts.find((a) => a.id === id);
  return account ? `${account.code} — ${account.name}` : id;
}

function accountTypeLabel(type: string | undefined) {
  if (!type) return "—";
  const labels: Record<string, string> = {
    asset: "Asset",
    liability: "Liability",
    equity: "Equity",
    revenue: "Revenue",
    expense: "Expense",
  };
  return labels[type] ?? type;
}

function FormField(props: {
  label: string;
  required?: boolean;
  field: {
    state: { meta: { errors: (string | { message: string } | undefined)[] } };
  };
  children: JSX.Element;
}) {
  const hasError = () => props.field.state.meta.errors.length > 0;
  const errorMessage = () => {
    const e = props.field.state.meta.errors[0];
    if (!e) return undefined;
    return typeof e === "string" ? e : e.message;
  };
  return (
    <TextField validationState={hasError() ? "invalid" : "valid"}>
      <TextFieldLabel>
        {props.label}
        <Show when={props.required}>
          <span class="text-destructive ml-0.5">*</span>
        </Show>
      </TextFieldLabel>
      {props.children}
      <TextFieldErrorMessage>{errorMessage()}</TextFieldErrorMessage>
    </TextField>
  );
}

function InfoRow(props: {
  label: string;
  value: string | null | undefined;
  multiline?: boolean;
}) {
  if (!props.value) return null;

  return (
    <div
      class={
        props.multiline
          ? "py-3.5 space-y-1.5"
          : "flex items-center justify-between py-3.5 gap-4"
      }
    >
      <p class="text-sm text-muted-foreground">{props.label}</p>
      <h6
        class={
          props.multiline
            ? "text-sm font-medium whitespace-pre-wrap"
            : "text-sm font-semibold text-right"
        }
      >
        {props.value}
      </h6>
    </div>
  );
}
